import {ImageProcessingNodes as Nodes} from "@cm/image-processing-nodes"
import {TextureType} from "@cm/material-nodes"
import type {
    Resolution,
    EncodedConvertedNode,
    ImageMapInfo,
    InputMapsInfo,
    InputMapsNodes,
    MapsNodesForCanonicalTextureTypes,
} from "#material-maps-exporter/core/types"
import {
    encodeConvertedNode,
    getImageMapInfos,
    getMapNodeOrProvideFallback,
    imageProcessingGraphForInputMaps,
    mix,
    convertNormal,
    provideFallbackMap,
    alphaFromMaskOrTransmission,
    ensureTargetSizeForImageMaps,
    anisotropyVectorToRotationAndStrength,
    getVectorAnisotropy,
} from "#material-maps-exporter/core/image-processing-graph"
import {estimateCommonSizeForImageMaps, getAveragePhysicalSizeInfo} from "#material-maps-exporter/core/gather-material-data"
import type {ConvertResult, Settings} from "#material-maps-exporter/targets/pbr/types"

export function estimateCommonSize(imageMapsInfo: ImageMapInfo[], resolution: Resolution) {
    const avgPhysicalSizeInfo = getAveragePhysicalSizeInfo(imageMapsInfo)
    const {widthPx, heightPx, dpi} = estimateCommonSizeForImageMaps(imageMapsInfo, resolution === "dpi72" ? 72 : "original", avgPhysicalSizeInfo)
    return {
        widthPx,
        heightPx,
        dpi,
    }
}

function ensureCommonSize(mapsInfo: InputMapsInfo, mapsNodes: InputMapsNodes, resolution: Resolution) {
    const imageMapsInfo = getImageMapInfos(mapsInfo)
    const retValue = estimateCommonSize(imageMapsInfo, resolution)
    const {widthPx, heightPx} = retValue
    ensureTargetSizeForImageMaps(mapsInfo, imageMapsInfo, mapsNodes, widthPx, heightPx)
    return retValue
}

function convertToCanonicalMaps(
    mapsNodes: InputMapsNodes,
    mapsInfo: InputMapsInfo,
    fallbackResX: number,
    fallbackResY: number,
): MapsNodesForCanonicalTextureTypes {
    function getSpecularAndMetalness(mapsNodes: InputMapsNodes): {
        specular: Nodes.ImageNode
        metalness: Nodes.ImageNode
    } {
        const types = Object.keys(mapsNodes)
        if (types.includes(TextureType.SpecularStrength) && types.includes(TextureType.Metalness)) {
            return {specular: mapsNodes[TextureType.SpecularStrength], metalness: mapsNodes[TextureType.Metalness]}
        } else if (types.includes(TextureType.F0)) {
            const f0 = mapsNodes[TextureType.F0]
            return {specular: Nodes.extractChannel(f0, 0), metalness: Nodes.extractChannel(f0, 1)}
        } else {
            return {
                specular: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.SpecularStrength, 0.04, "linear"),
                metalness: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Metalness, 0, "linear"),
            }
        }
    }

    function getAlphaAndTransmission(mapsNodes: InputMapsNodes): {
        alpha: Nodes.ImageNode
        transmission: Nodes.ImageNode
    } {
        return {
            alpha: alphaFromMaskOrTransmission(mapsNodes, mapsInfo) ?? provideFallbackMap(fallbackResX, fallbackResY, "linear", 1),
            transmission: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Transmission, [0, 0, 0], "linear"),
        }
    }

    const {specular, metalness} = getSpecularAndMetalness(mapsNodes)
    const anisotropy = getVectorAnisotropy(mapsNodes, fallbackResX, fallbackResY)
    const {alpha, transmission} = getAlphaAndTransmission(mapsNodes)

    return {
        diffuse: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Diffuse, [0, 0, 0], "linear"),
        normal: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Normal, [0.5, 0.5, 1.0], "linear"),
        roughness: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Roughness, 1, "linear"),
        "specular-strength": specular,
        metalness,
        anisotropy,
        displacement: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Displacement, 0, "linear"),
        alpha,
        transmission,
    }
}

function getMetalnessRoughnessMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, settings: Settings, dpi: number | undefined) {
    const diffuse = canonicalMapsNodes["diffuse"]
    const metalness = Nodes.math("clip01", canonicalMapsNodes["metalness"])
    const roughness = Nodes.math("clip01", canonicalMapsNodes["roughness"])
    const specular = Nodes.math("clip01", canonicalMapsNodes["specular-strength"])

    return {
        diffuse: encodeConvertedNode(diffuse, settings.format, dpi, true, false),
        specular: encodeConvertedNode(specular, settings.format, dpi),
        metalness: encodeConvertedNode(metalness, settings.format, dpi),
        roughness: encodeConvertedNode(roughness, settings.format, dpi),
    }
}

function getSpecularGlossinessMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, settings: Settings, dpi: number | undefined) {
    const specular = Nodes.math("clip01", canonicalMapsNodes["specular-strength"])
    const metalness = Nodes.math("clip01", canonicalMapsNodes["metalness"])
    const ones = Nodes.math("constLike", specular, 1.0)

    const specular_ms = Nodes.math("*", Nodes.math("max", specular, 0), 0.08)
    const f0_scalar = Nodes.math("min", Nodes.math("max", mix(specular_ms, ones, metalness), 1e-6), 1)
    const f0_s_ = Nodes.math("sqrt", f0_scalar)
    const ior = Nodes.math("/", Nodes.math("-", ones, f0_s_), Nodes.math("+", ones, f0_s_))

    const baseColor = canonicalMapsNodes["diffuse"]
    const diffuse = mix(baseColor, 0, metalness)
    const reflect = Nodes.math("/", mix(specular_ms, baseColor, metalness), f0_scalar)
    const gloss = Nodes.math("-", ones, canonicalMapsNodes["roughness"])

    return {
        diffuse: encodeConvertedNode(diffuse, settings.format, dpi, true, false),
        reflect: encodeConvertedNode(reflect, settings.format, dpi, true, false),
        gloss: encodeConvertedNode(gloss, settings.format, dpi),
        "fresnel-ior": encodeConvertedNode(ior, settings.format, dpi),
    }
}

function getWorkflowMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, settings: Settings, dpi: number | undefined) {
    if (settings.workflow === "metalnessRoughness") return getMetalnessRoughnessMaps(canonicalMapsNodes, settings, dpi)
    else if (settings.workflow === "specularGlossiness") return getSpecularGlossinessMaps(canonicalMapsNodes, settings, dpi)
    else throw Error(`Unsupported workflow: ${settings.workflow}`)
}

function getAlphaAndTransmissionMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, settings: Settings, dpi: number | undefined) {
    return {
        transmission: encodeConvertedNode(canonicalMapsNodes["transmission"], settings.format, dpi, true, false),
        alpha: encodeConvertedNode(canonicalMapsNodes["alpha"], settings.format, dpi),
    }
}

function convertToCoronaMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, settings: Settings, dpi: number | undefined): ConvertResult<"corona"> {
    const vector = anisotropyVectorToRotationAndStrength(canonicalMapsNodes["anisotropy"])
    vector.strength = Nodes.math("+", Nodes.math("*", vector.strength, 0.5), 0.5) //Map to 0.5..1.0 range

    const normal = convertNormal(canonicalMapsNodes["normal"], settings.normalY, "y+down")

    return {
        ...getWorkflowMaps(canonicalMapsNodes, settings, dpi),
        normal: encodeConvertedNode(normal, settings.format, dpi, false, false),
        "anisotropy-strength": encodeConvertedNode(vector.strength, settings.format, dpi),
        "anisotropy-rotation": encodeConvertedNode(vector.rotation, settings.format, dpi),
        displacement: encodeConvertedNode(canonicalMapsNodes["displacement"], settings.format, dpi, false, true),
        ...getAlphaAndTransmissionMaps(canonicalMapsNodes, settings, dpi),
    }
}

function convertToVrayMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, settings: Settings, dpi: number | undefined): ConvertResult<"vray"> {
    const {rotation, strength} = anisotropyVectorToRotationAndStrength(canonicalMapsNodes["anisotropy"])

    const normal = convertNormal(canonicalMapsNodes["normal"], settings.normalY, "y+down")

    return {
        ...getWorkflowMaps(canonicalMapsNodes, settings, dpi),
        normal: encodeConvertedNode(normal, settings.format, dpi, false, false),
        "anisotropy-strength": encodeConvertedNode(strength, settings.format, dpi),
        "anisotropy-rotation": encodeConvertedNode(rotation, settings.format, dpi),
        displacement: encodeConvertedNode(canonicalMapsNodes["displacement"], settings.format, dpi, false, true),
        ...getAlphaAndTransmissionMaps(canonicalMapsNodes, settings, dpi),
    }
}

function convertToCyclesMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, settings: Settings, dpi: number | undefined): ConvertResult<"cycles"> {
    const vector = anisotropyVectorToRotationAndStrength(canonicalMapsNodes["anisotropy"])
    vector.rotation = Nodes.math("+", Nodes.math("*", vector.rotation, -1), 1) // CCW rotation

    const normal = convertNormal(canonicalMapsNodes["normal"], settings.normalY, "y+up")

    return {
        ...getWorkflowMaps(canonicalMapsNodes, settings, dpi),
        normal: encodeConvertedNode(normal, settings.format, dpi, false, false),
        "anisotropy-strength": encodeConvertedNode(vector.strength, settings.format, dpi),
        "anisotropy-rotation": encodeConvertedNode(vector.rotation, settings.format, dpi),
        displacement: encodeConvertedNode(canonicalMapsNodes["displacement"], settings.format, dpi, false, true),
        ...getAlphaAndTransmissionMaps(canonicalMapsNodes, settings, dpi),
    }
}

function convertToCmMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, settings: Settings, dpi: number | undefined): ConvertResult<"cm"> {
    const normal = convertNormal(canonicalMapsNodes["normal"], settings.normalY, "y+down")

    return {
        ...getWorkflowMaps(canonicalMapsNodes, settings, dpi),
        normal: encodeConvertedNode(normal, settings.format, dpi, false, false),
        anisotropy: encodeConvertedNode(canonicalMapsNodes["anisotropy"], settings.format, dpi, false, false),
        displacement: encodeConvertedNode(canonicalMapsNodes["displacement"], settings.format, dpi, false, true),
        ...getAlphaAndTransmissionMaps(canonicalMapsNodes, settings, dpi),
    }
}

export function imageProcessingGraphForPbrExport(settings: Settings, mapsInfo: InputMapsInfo): Nodes.Struct<Record<string, EncodedConvertedNode>> {
    const mapsNodes = imageProcessingGraphForInputMaps(mapsInfo)
    const {widthPx, heightPx, dpi} = ensureCommonSize(mapsInfo, mapsNodes, settings.resolution)
    const canonicalMapsNodes = convertToCanonicalMaps(mapsNodes, mapsInfo, widthPx, heightPx)

    let conversionFn: typeof convertToCoronaMaps | typeof convertToVrayMaps | typeof convertToCyclesMaps | typeof convertToCmMaps
    switch (settings.engine) {
        case "corona":
            conversionFn = convertToCoronaMaps
            break
        case "vray":
            conversionFn = convertToVrayMaps
            break
        case "cycles":
            conversionFn = convertToCyclesMaps
            break
        case "cm":
            conversionFn = convertToCmMaps
            break
        default:
            throw Error(`Unsupported engine: ${settings.engine}`)
    }

    return Nodes.struct(conversionFn(canonicalMapsNodes, settings, dpi))
}
